/* eslint-disable @typescript-eslint/no-explicit-any */
import { createRoot } from "react-dom/client";
import {
  ComponentCollection,
  type ICustomQuestionTypeConfiguration,
  type IPanel,
  type IQuestion,
  type PanelModel,
  type Question,
  type QuestionCompositeModel,
  type QuestionCustomModel,
  type QuestionDropdownModel,
  type QuestionPanelDynamicModel,
  type QuestionRadiogroupModel,
  Serializer,
  type SurveyModel,
} from "survey-core";
import {
  CUSTOM_TYPE_PROFILE_SEARCH,
  ProfileSearchMultipleQuestionType,
  ProfileSearchMultipleQuestionProperties,
} from "shared/components/survey-js-form/questions/profile-search/profileSearchConstants";
import {
  type ProfileSearchMultipleQuestionData,
  type IRoleItemValue,
} from "shared/components/survey-js-form/questions/profile-search/ProfileSearchQuestionTypes";
import { removeHTMLTags } from "shared/components/survey-js-form/utils/removeHTMLTags";
import { coAuthorBlockTemplateUtil } from "shared/components/template/coAuthorBlockTemplateUtil";
import { defaultEmailFromProfile } from "shared/helpers/profile/defaultEmailFromProfile";
import { getDisplayNameHelper } from "shared/helpers/profile/getDisplayNameHelper";
import { type ProfileView } from "shared/mappers/database/profile/profile";
import { type SessionView } from "shared/mappers/database/session/session";

const MOVE_BUTTONS_HTML = `<div class='flex items-center w-fit gap-x-2'>
                            <button data-testid='moveUpButton' type='button' title='Move Up' class='moveUpButton !p-0.5 disabled:opacity-50 disabled:cursor-not-allowed'>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 15l-6-6-6 6"/></svg>
                            </button>
                            <button data-testid='moveDownButton' type='button' title='Move Down' class='moveDownButton !p-0.5 disabled:opacity-50 disabled:cursor-not-allowed'>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
                            </button>
                          </div>`;

const REMOVE_BUTTON_HTML = `<button data-testid='removePanelButton' type='button' title='Remove' class='removePanelButton'>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                                <path d="M18 6L6 18M6 6l12 12"/>
                              </svg>
                            </button>`;

const registerPropertiesProfileSearchMultiple = async () => {
  Serializer.addProperty("panel", {
    name: "panelTag",
    type: "text",
  });
  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties.roleTitle,
    displayName: "Title for the role column",
    type: "text",
    category: "general",
  });
  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties.profileSearchTitle,
    displayName: "Title for the profile search question",
    type: "text",
    category: "general",
  });
  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties
      .profileSearchQuestionProperties.directoryId,
    displayName: "Choose a directory to search for profiles",
    type: "dropdown",
    isRequired: true,
    choices: [],
    category: "general",
  });
  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties
      .profileSearchQuestionProperties.newProfileWorkflowId,
    displayName: "Choose a workflow to create a new profile",
    type: "dropdown",
    isRequired: true,
    choices: [],
    category: "general",
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties
      .profileSearchQuestionProperties.preventMultipleAssignment,
    displayName: "Prevent Multiple Assignment For Same Profile",
    type: "boolean",
    category: "general",
    default: false,
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties
      .profileSearchQuestionProperties.allowInlineParticipantEntry,
    displayName:
      "Allow participants to be added in-line without creating profiles",
    type: "boolean",
    category: "general",
    default: false,
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties
      .profileSearchQuestionProperties.overrideAffiliation,
    displayName: "Override affiliation",
    type: "boolean",
    category: "general",
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties
      .profileSearchQuestionProperties.allowRemovePanel,
    displayName: "Allow remove panel",
    type: "boolean",
    category: "general",
  });

  const buildRolesItemValues = () => {
    const roles = [
      {
        value: "submitter",
        text: "Submitter",
      },
    ];
    roles.push({ value: "chair", text: "Chair" });
    roles.push({ value: "cochair", text: "Co-Chair" });
    roles.push({ value: "speaker", text: "Speaker" });
    roles.push({ value: "moderator", text: "Moderator" });
    roles.push({ value: "coauthor", text: "Coauthor" });
    return roles;
  };

  const roleItemValues = buildRolesItemValues();
  Serializer.addClass(
    "roleitemvalue",
    [
      {
        name: ProfileSearchMultipleQuestionProperties.roleItemValueProperties
          .value,
        type: "dropdown",
        choices: roleItemValues,
        onSettingValue(obj, value) {
          const matchingRole = roleItemValues.find(
            (role) => role.value === value,
          );
          if (!matchingRole) {
            return value;
          }
          obj.text = matchingRole.text;
          return value;
        },
      },
      { name: "text", type: "string", showMode: "form" },
      {
        name: ProfileSearchMultipleQuestionProperties.roleItemValueProperties
          .min,
        type: "number",
        showMode: "form",
        displayName: "Minimum (1 for required)",
      },
      {
        name: ProfileSearchMultipleQuestionProperties.roleItemValueProperties
          .max,
        type: "number",
        showMode: "form",
        displayName: "Maximum (0 or blank for unlimited)",
      },
      {
        name: ProfileSearchMultipleQuestionProperties.roleItemValueProperties
          .trackPositions,
        type: "boolean",
        showMode: "form",
        displayName: "Track Positions",
      },
      {
        name: ProfileSearchMultipleQuestionProperties.roleItemValueProperties
          .allowChangePosition,
        type: "boolean",
        showMode: "form",
        displayName: "Allow Change Position",
      },
      {
        name: ProfileSearchMultipleQuestionProperties.roleItemValueProperties
          .allowChangeProfile,
        type: "boolean",
        showMode: "form",
        displayName: "Allow Change Profile",
      },
    ],
    undefined,
    "itemvalue",
  );
  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties.role,
    displayName: "Select role for the profile",
    type: "roleitemvalue[]",
    category: "general",
    choices: roleItemValues,
  });
  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: ProfileSearchMultipleQuestionProperties.autoAssignSubmitter,
    displayName: "Auto assign submitter",
    type: "dropdown",
    category: "general",
    choices: roleItemValues,
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: "emailRequired",
    displayName: "Email",
    type: "boolean",
    category: "Create Profile Required Fields",
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: "affiliationRequired",
    displayName: "Affiliation",
    type: "boolean",
    category: "Create Profile Required Fields",
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: "firstNameRequired",
    displayName: "First name",
    type: "boolean",
    category: "Create Profile Required Fields",
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: "lastNameRequired",
    displayName: "Last name",
    type: "boolean",
    category: "Create Profile Required Fields",
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: "coauthorBlock",
    displayName: "Add coauthor block",
    type: "boolean",
    default: false,
    category: "Co-Author Block",
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: "styleSubmitter",
    displayName: "Add styling to submitter name",
    type: "boolean",
    default: true,
    category: "Co-Author Block",
    visibleIf: (obj) => obj.coauthorBlock === true,
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: "submitterClassName",
    displayName: "Custom CSS class for submitter name",
    type: "string",
    default: "font-bold text-primary-900",
    category: "Co-Author Block",
    visibleIf: (obj) => obj.styleSubmitter === true,
  });

  Serializer.addProperty(ProfileSearchMultipleQuestionType, {
    name: "blockContainerClassName",
    displayName: "Custom CSS class for coauthor block container",
    type: "string",
    default: "",
    category: "Co-Author Block",
    visibleIf: (obj) => obj.coauthorBlock === true,
  });
};

export type ProfileSearchMultipleQuestionProps = {
  session: SessionView;
};
export const generatedPanelName = "generatedRolePanel";
export const autoAssignedProfileQuestion = "autoAssignedProfile";
export const ProfileSearchMultipleQuestion = async ({
  session,
}: ProfileSearchMultipleQuestionProps) => {
  await registerPropertiesProfileSearchMultiple();

  const getBlockClassName = (
    vals: ProfileSearchMultipleQuestionData[],
    className: string,
  ) => {
    if (!vals) {
      return;
    }
    return vals.some((v) => v?.profilesearch !== undefined)
      ? removeHTMLTags(className)
      : "hidden";
  };

  const generateCoAuthorBlockHTML = ({
    container,
    question,
    submitterClassName,
    blockContainerClassName,
    profile,
    styleSubmitter,
  }: {
    container: HTMLElement;
    question: QuestionCustomModel;
    submitterClassName: string;
    blockContainerClassName: string;
    profile: ProfileView;
    styleSubmitter: boolean;
  }) => {
    container.innerHTML = coAuthorBlockTemplateUtil({
      coAuthorArrayPath: "coAuthorArray",
      authorFullNamePath: "profilesearch.displayName",
      authorProfileIdPath: "profilesearch.profileId",
      authorAffiliationPath: "profilesearch.affiliation",
      profileNeedsStylingPath: "submitterProfileId",
      positionPath: "position",
      profileNeedsStylingClassName: submitterClassName,
      blockClassName: getBlockClassName(
        question.value,
        blockContainerClassName,
      ),
      data: {
        coAuthorArray: question.value,
        submitterProfileId: styleSubmitter ? profile?.profileId : null,
      },
    });
  };

  const updateAllPanelsUI = async (question: QuestionCustomModel) => {
    const panelDynamic = question.contentQuestion as QuestionPanelDynamicModel;
    const currentQuestionValue = (question.value ||
      []) as ProfileSearchMultipleQuestionData[];
    const configuredRolesList =
      <IRoleItemValue[]>(
        question.getPropertyValue(ProfileSearchMultipleQuestionProperties.role)
      ) ?? [];

    const newProfileWorkflowId = question.getPropertyValue(
      ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
        .newProfileWorkflowId,
    );

    const allowRemovePanel = question.getPropertyValue(
      ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
        .allowRemovePanel,
    );

    const roleCounters: Record<string, number> = {};
    currentQuestionValue.forEach((pData) => {
      if (pData.role) {
        roleCounters[pData.role] = (roleCounters[pData.role] || 0) + 1;
      }
    });

    if (!session.adminMode) {
      const canAddMoreBasedOnRoleLimits = configuredRolesList.some((r) => {
        const count = roleCounters[r.value] || 0;
        return count < (r.max ?? Infinity);
      });

      const globalMaxPanelCount = panelDynamic.maxPanelCount ?? Infinity;
      panelDynamic.allowAddPanel =
        canAddMoreBasedOnRoleLimits &&
        currentQuestionValue.length < globalMaxPanelCount &&
        configuredRolesList.length > 0;
    }

    panelDynamic.panels.forEach((panel, panelIndex) => {
      const roleQuestion = panel.getQuestionByName("role");
      const rolePanelHtml = panel.getQuestionByName("rolePanelHtml");
      const moveButtonsHtmlQuestion =
        panel.getQuestionByName("moveButtonsHtml");
      const removePanelButtonHtml = panel.getQuestionByName(
        "removePanelButtonHtml",
      );

      if (!roleQuestion || !rolePanelHtml || !moveButtonsHtmlQuestion) {
        console.warn(
          "ProfileSearchMultiple: Core questions not found in panel template for panel at index",
          panelIndex,
        );
      }

      const panelData = currentQuestionValue[panelIndex];

      if (panelData) {
        const roleDefinition = configuredRolesList.find(
          (r) => r.value === panelData.role,
        );

        if (panelData.role && roleDefinition) {
          roleQuestion.visible = false;
          rolePanelHtml.visible = true;
          moveButtonsHtmlQuestion.visible = true;

          const hasMoreThanMinimumRoles =
            roleCounters[panelData.role] > (roleDefinition.min ?? 0);

          const isProfileNotSelected = !panelData.profilesearch?.profileId;

          removePanelButtonHtml.visible =
            hasMoreThanMinimumRoles &&
            (allowRemovePanel ||
              !newProfileWorkflowId ||
              isProfileNotSelected ||
              session.adminMode);

          const displayPosition = panelData.position;
          rolePanelHtml.html = `<p style="font-size: 16px; font-weight: 600;">${
            roleDefinition.text
          }${
            roleDefinition.trackPositions && displayPosition !== undefined
              ? " #" + (displayPosition + 1)
              : ""
          }</p>`;
          if (
            !roleDefinition.allowChangePosition ||
            !roleDefinition.trackPositions
          ) {
            moveButtonsHtmlQuestion.html = `<div class="hidden">${MOVE_BUTTONS_HTML}</div>`;
          } else {
            moveButtonsHtmlQuestion.html = MOVE_BUTTONS_HTML;
          }
        } else {
          roleQuestion.visible = true;
          rolePanelHtml.visible = false;
          rolePanelHtml.html = "";
          moveButtonsHtmlQuestion.visible = true;
        }

        if (roleQuestion.visible && !session.adminMode) {
          const allSelectedRolesInFinalOrder = currentQuestionValue
            .map((item) => item.role)
            .filter(Boolean) as string[];

          const availableRoles = configuredRolesList.filter((r) => {
            const count = allSelectedRolesInFinalOrder.filter(
              (roleValue) => roleValue === r.value,
            ).length;
            const isCurrentPanelThisRole = panelData.role === r.value;
            if (isCurrentPanelThisRole) {
              return true;
            }
            return count < (r.max ?? Infinity);
          });
          roleQuestion.choices = availableRoles.map((choice) => ({
            value: choice.value,
            text: choice.text,
          }));
          if (
            configuredRolesList.length > 1 &&
            availableRoles.length === 1 &&
            !panelData.role
          ) {
            const lastAvailableRole = availableRoles[0];

            const updatedValue = [...currentQuestionValue];
            updatedValue[panelIndex] = {
              ...updatedValue[panelIndex],
              role: lastAvailableRole.value,
            };

            // Set new value asynchronously to avoid infinite loop.
            setTimeout(() => {
              question.value = updatedValue;
            }, 0);
          }
        }
      } else {
        roleQuestion.visible = true;
        rolePanelHtml.visible = false;
        moveButtonsHtmlQuestion.visible = false;
      }
    });
  };

  const setupCustomRemoveButtons = async (
    htmlElement: HTMLElement,
    question: QuestionCustomModel,
  ) => {
    const panelDynamic = question.contentQuestion as QuestionPanelDynamicModel;

    panelDynamic.panels.forEach((panel) => {
      const panelContentElement = htmlElement.querySelector(
        `#${panel.contentId}`,
      );

      const removeButton = panelContentElement?.querySelector(
        ".removePanelButton",
      ) as HTMLButtonElement;

      if (removeButton) {
        const newRemoveButton = removeButton.cloneNode(
          true,
        ) as HTMLButtonElement;
        removeButton.parentNode!.replaceChild(newRemoveButton, removeButton);

        newRemoveButton.onclick = () => {
          panelDynamic.removePanel(panel);
        };
      }
    });
  };

  const checkMoveConditions = (
    array: any[],
    index: number,
    direction: "up" | "down",
  ): boolean => {
    if (!array) {
      return false;
    }

    const currentItem = array[index];
    if (!currentItem) {
      return false;
    }

    const adjacentIndex = direction === "up" ? index - 1 : index + 1;
    const adjacentItem = array[adjacentIndex];

    if (direction === "up" && index === 0) {
      return false;
    }
    if (direction === "down" && index === array.length - 1) {
      return false;
    }

    // Check for adjacent item existence and role match
    return !!adjacentItem && currentItem.role === adjacentItem.role;
  };

  const performSwapAndUpdate = (
    arrayCopy: any[],
    index: number,
    direction: "up" | "down",
  ): any[] => {
    const adjacentIndex = direction === "up" ? index - 1 : index + 1;

    // Save references to elements BEFORE swapping to work with positions
    const itemAtIndex = arrayCopy[index];
    const itemAtAdjacentIndex = arrayCopy[adjacentIndex];

    // swap
    arrayCopy[index] = itemAtAdjacentIndex;
    arrayCopy[adjacentIndex] = itemAtIndex;

    const originalPositionAtIndex = itemAtIndex.position;
    arrayCopy[adjacentIndex].position = itemAtAdjacentIndex.position;
    arrayCopy[index].position = originalPositionAtIndex;

    return arrayCopy;
  };

  const setupMoveButtons = async (
    htmlElement: HTMLElement,
    question: QuestionCustomModel,
  ) => {
    const panelDynamic = question.contentQuestion as QuestionPanelDynamicModel;

    panelDynamic.panels.forEach((panel, panelIndex) => {
      const panelContentElement = htmlElement.querySelector(
        `#${panel.contentId}`,
      ) as HTMLElement;
      if (!panelContentElement) {
        console.warn(
          `ProfileSearchMultiple: Could not find panel content element for move buttons, panelId: ${panel.id}`,
        );
        return;
      }

      const currentQuestionValue =
        question.value as ProfileSearchMultipleQuestionData[];
      const moveUpButton = panelContentElement.querySelector(
        ".moveUpButton",
      ) as HTMLButtonElement;
      const moveDownButton = panelContentElement.querySelector(
        ".moveDownButton",
      ) as HTMLButtonElement;

      if (moveUpButton) {
        const canMoveUp = checkMoveConditions(
          currentQuestionValue,
          panelIndex,
          "up",
        );
        moveUpButton.disabled = !canMoveUp;
        const newMoveUpButton = moveUpButton.cloneNode(
          true,
        ) as HTMLButtonElement;
        moveUpButton.parentNode!.replaceChild(newMoveUpButton, moveUpButton);
        newMoveUpButton.onclick = () => {
          const freshQuestionValue = question.value;
          if (!checkMoveConditions(freshQuestionValue, panelIndex, "up")) {
            newMoveUpButton.disabled = true;
            return;
          }
          // Create a copy of the array BEFORE modification
          const tempArray = [...freshQuestionValue];
          const updatedArray = performSwapAndUpdate(
            tempArray,
            panelIndex,
            "up",
          );
          question.value = updatedArray;
        };
      }

      if (moveDownButton) {
        const canMoveDown = checkMoveConditions(
          currentQuestionValue,
          panelIndex,
          "down",
        );
        moveDownButton.disabled = !canMoveDown;
        const newMoveDownButton = moveDownButton.cloneNode(
          true,
        ) as HTMLButtonElement;
        moveDownButton.parentNode!.replaceChild(
          newMoveDownButton,
          moveDownButton,
        );
        newMoveDownButton.onclick = () => {
          const freshQuestionValue = question.value;
          if (!checkMoveConditions(freshQuestionValue, panelIndex, "down")) {
            newMoveDownButton.disabled = true;
            return;
          }
          // Create a copy of the array BEFORE modification
          const tempArray = [...freshQuestionValue];
          const updatedArray = performSwapAndUpdate(
            tempArray,
            panelIndex,
            "down",
          );
          question.value = updatedArray;
        };
      }
    });
  };

  const multiple_profile_search_object: ICustomQuestionTypeConfiguration = {
    name: ProfileSearchMultipleQuestionType,
    defaultQuestionTitle: "Multiple Profile Search",
    inheritBaseProps: [
      "panelAddText",
      // "onPanelAdded",
      ...(session.adminMode ? [] : ["maxPanelCount"]),
    ],
    questionJSON: {
      type: "paneldynamic",
      name: "multiple_profile_search",
      title: "Multiple Profile Search",
      keyName: "panelId",
      titleLocation: "hidden",
      panelRemoveButtonLocation: "hidden",
      allowRemovePanel: false,
      showNumber: true,
      templateElements: [
        {
          type: "number",
          name: "submissionParticipantId",
          visible: false,
        },
        {
          type: "html",
          name: "moveButtonsHtml",
          titleLocation: "hidden",
          startWithNewLine: false,
          html: MOVE_BUTTONS_HTML,
        },
        {
          type: "panel",
          name: "rolePanel",
          startWithNewLine: false,
          maxWidth: 400,
          elements: [
            {
              type: "dropdown",
              name: "role",
              title: "Role",
              visible: true,
              errorLocation: "bottom",
              validators: [
                {
                  type: "expression",
                  expression: "checkRoleExists({role})",
                  text: "You must select a role for this profile.",
                },
                {
                  type: "expression",
                  expression: "checkRequiredRolesMaximum({role})",
                  text: "You have exceeded the maximum number of this role allowed.",
                },
              ],
              choices: [],
            },
            {
              type: "number",
              name: "position",
              startWithNewLine: false,
              visible: false,
            },
            {
              type: "html",
              name: "rolePanelHtml",
              titleLocation: "hidden",
              html: "",
              visible: false,
              startWithNewLine: false,
            },
            {
              type: "dropdown",
              name: "coauthorBlock",
              title: "Block Position",
              visible: false,
              choices: [],
            },
          ],
        },
        {
          startWithNewLine: false,
          isRequired: true,
          titleLocation: "hidden",
          requiredErrorText: "You must select a profile.",
          name: CUSTOM_TYPE_PROFILE_SEARCH,
          type: CUSTOM_TYPE_PROFILE_SEARCH,
          // validators: [
          //   {
          //     type: "expression",
          //     expression: "checkForDuplicateProfileIds({profile_id})",
          //     text: "You cannot assign the same profile to the same role.",
          //   },
          //   {
          //     type: "expression",
          //     expression: "checkProfileIdSet({profile_id})",
          //     text: "A profile must be selected.",
          //   },
          // ],
        },

        {
          type: "html",
          name: "removePanelButtonHtml",
          titleLocation: "hidden",
          startWithNewLine: false,
          visible: false,
          html: REMOVE_BUTTON_HTML,
        },
      ],
    },

    onInit: () => {
      registerPropertiesProfileSearchMultiple();
    },
    onPropertyChanged: (
      question: Question,
      propertyName: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      newValue: any,
    ): void => {
      const panelDynamic = <QuestionPanelDynamicModel>question.contentQuestion;

      if (panelDynamic) {
        panelDynamic.panels?.forEach((panel) => {
          panel.panelTag = "profilesearchmultiple";
        });
      }

      const rolePanel = <PanelModel>(
        panelDynamic?.template.getElementByName("rolePanel")
      );
      const coauthorBlockQuestion = <QuestionDropdownModel>(
        rolePanel?.getElementByName("coauthorBlock")
      );

      if (coauthorBlockQuestion) {
        coauthorBlockQuestion.choices = Array.from(
          { length: panelDynamic.panelCount },
          (_, index) => {
            return {
              value: (index + 1).toString(),
              text: (index + 1).toString(),
            };
          },
        );
      }
    },
    onLoaded: (question: QuestionCustomModel) => {
      const survey = question.getSurvey();

      //Get the roles from the question properties
      const roles =
        <IRoleItemValue[]>(
          question.getPropertyValue(
            ProfileSearchMultipleQuestionProperties.role,
          )
        ) ?? [];

      //Set properties of the ProfileSearchQuestion
      const panelTag = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .panelTag,
      );

      const directoryId = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .directoryId,
      );
      const newProfileWorkflowId = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .newProfileWorkflowId,
      );

      const autoAssignSubmitter = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.autoAssignSubmitter,
      );

      const emailRequired = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .emailRequired,
      );

      const affiliationRequired = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .affiliationRequired,
      );

      const firstNameRequired = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .firstNameRequired,
      );

      const lastNameRequired = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .lastNameRequired,
      );

      const preventMultipleAssignment = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .preventMultipleAssignment,
      );

      const overrideAffiliation = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .overrideAffiliation,
      );

      const allowInlineParticipantEntry = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .allowInlineParticipantEntry,
      );

      const allowRemovePanel = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .allowRemovePanel,
      );

      const panelDynamic = <QuestionPanelDynamicModel>question.contentQuestion;

      const profilesearchQuestion = <QuestionCompositeModel>(
        panelDynamic.template.getElementByName(CUSTOM_TYPE_PROFILE_SEARCH)
      );

      //Attach the properties to the profile search question inside the panel dynamic template
      if (profilesearchQuestion) {
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.panelTag,
          panelTag,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.directoryId,
          directoryId,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.newProfileWorkflowId,
          newProfileWorkflowId,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties.autoAssignSubmitter,
          autoAssignSubmitter,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.emailRequired,
          emailRequired,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.affiliationRequired,
          affiliationRequired,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.firstNameRequired,
          firstNameRequired,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.lastNameRequired,
          lastNameRequired,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.preventMultipleAssignment,
          preventMultipleAssignment,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.overrideAffiliation,
          overrideAffiliation,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.allowInlineParticipantEntry,
          allowInlineParticipantEntry,
        );
        profilesearchQuestion.setPropertyValue(
          ProfileSearchMultipleQuestionProperties
            .profileSearchQuestionProperties.allowRemovePanel,
          allowRemovePanel,
        );
      }

      //Fetch the role panel from the panel dynamic template
      const rolePanel = <PanelModel>(
        panelDynamic.template.getElementByName("rolePanel")
      );
      const moveButtonsHtml =
        panelDynamic.template.getElementByName("moveButtonsHtml");

      if (roles && Array.isArray(roles) && roles.length > 0) {
        const roleRadiogroupQuestion = <QuestionRadiogroupModel>(
          rolePanel.getElementByName("role")
        );

        roleRadiogroupQuestion.choices = roles.map((role: IRoleItemValue) => {
          return {
            value: role.value,
            text: role.text,
          };
        });
        const roleTitle = question.getPropertyValue(
          ProfileSearchMultipleQuestionProperties.roleTitle,
        );

        const coauthorBlockQuestion = <QuestionDropdownModel>(
          rolePanel.getElementByName("coauthorBlock")
        );

        if (roleTitle) {
          roleRadiogroupQuestion.validators.forEach((validator) => {
            validator.text = validator.text.replace(
              "role",
              question.getPropertyValue(
                ProfileSearchMultipleQuestionProperties.roleTitle,
              ) ?? "role",
            );
          });
        }

        coauthorBlockQuestion.titleLocation = "left";

        roles.forEach((role: IRoleItemValue) => {
          //add panels for each role that has a min > 0
          if (role.min && role.min > 0) {
            //for each till the min is reach add a panel with the role selected and set to readonly
            const minPanelCount = role.min;
            for (let i = 0; i < minPanelCount; i++) {
              panelDynamic.addPanel();
              const newPanel =
                panelDynamic.panels[panelDynamic.panels.length - 1];
              if (!newPanel) {
                console.error("Failed to create dynamic panel");
                return;
              }
              newPanel.name = generatedPanelName;

              const roleQuestion = newPanel.getQuestionByName("role");
              const rolePanelHtml = newPanel.getQuestionByName("rolePanelHtml");
              const moveButtonsHtml =
                newPanel.getQuestionByName("moveButtonsHtml");

              roleQuestion.visible = false;

              if (!role.allowChangePosition || !role.trackPositions) {
                moveButtonsHtml.html = `<div class="hidden">${moveButtonsHtml.html}</div>`;
              }

              rolePanelHtml.visible = true;
              rolePanelHtml.html = `<p style="font-size: 16px; font-weight: 600;">${
                role.text
              }${role.trackPositions ? " #" + (i + 1) : ""}</p>`;

              roleQuestion.value = role.value;
              const profileSearchQuestion = newPanel.getQuestionByName(
                CUSTOM_TYPE_PROFILE_SEARCH,
              );
              if (role.value === autoAssignSubmitter && profileSearchQuestion) {
                profileSearchQuestion.readOnly = true;
              }
            }
          }
        });

        if (Array.isArray(question.value)) {
          const currentQuestionValue = question.value;

          const roleCounters: Record<string, number> = {};

          const processedInitialValue = currentQuestionValue.map((item) => {
            const currentRole = item.role;
            if (currentRole) {
              if (roleCounters[currentRole] === undefined) {
                roleCounters[currentRole] = 0;
              }
              return { ...item, position: roleCounters[currentRole]++ };
            }
            return item;
          });
          question.value = processedInitialValue;
        }
      } else {
        rolePanel.visible = false;
        moveButtonsHtml.visible = false;
      }

      if (survey && panelDynamic) {
        const existingDynamicPanelAddedHandler = survey.dynamicPanelAdded;

        survey.dynamicPanelAdded = (
          triggeredQuestionArg: IQuestion,
          panelIndexArg: number,
          addedPanelArg: IPanel,
          updateIndexesArg: boolean,
        ) => {
          if (existingDynamicPanelAddedHandler) {
            existingDynamicPanelAddedHandler.call(
              survey,
              triggeredQuestionArg,
              panelIndexArg,
              addedPanelArg,
              updateIndexesArg,
            );
          }

          if (triggeredQuestionArg === panelDynamic) {
            let identifiedAddedPanel: PanelModel | undefined = undefined;
            const qpd = triggeredQuestionArg as QuestionPanelDynamicModel;

            if (addedPanelArg) {
              identifiedAddedPanel = addedPanelArg as PanelModel;
            } else if (
              typeof panelIndexArg === "number" &&
              qpd.panels &&
              qpd.panels[panelIndexArg]
            ) {
              identifiedAddedPanel = qpd.panels[panelIndexArg];
            }

            // FALLBACK: if event arguments didn't help
            if (!identifiedAddedPanel && qpd.panelCount > 0) {
              const lastPanelIndex = qpd.panelCount - 1;
              if (qpd.panels && qpd.panels[lastPanelIndex]) {
                const potentialPanel = qpd.panels[lastPanelIndex];
                const roleQuestion = potentialPanel.getQuestionByName(
                  "role",
                ) as QuestionRadiogroupModel;

                if (roleQuestion && !roleQuestion.value) {
                  identifiedAddedPanel = potentialPanel;
                } else {
                  console.warn(
                    `Fallback: Last panel at index ${lastPanelIndex} already has a role ('${roleQuestion?.value}') or role question is missing. Not considered new for this auto-assignment logic.`,
                  );
                }
              } else {
                console.warn(
                  `Fallback: panelCount is ${qpd.panelCount}, but panel not found at lastPanelIndex ${lastPanelIndex} or panels collection is missing.`,
                );
              }
            }

            if (identifiedAddedPanel) {
              identifiedAddedPanel.name = generatedPanelName;
              const configuredRoles =
                <IRoleItemValue[]>(
                  question.getPropertyValue(
                    ProfileSearchMultipleQuestionProperties.role,
                  )
                ) ?? [];

              if (configuredRoles.length === 1) {
                const singleRole = configuredRoles[0];
                const roleQuestionInAddedPanel =
                  identifiedAddedPanel.getQuestionByName(
                    "role",
                  ) as QuestionRadiogroupModel;

                if (roleQuestionInAddedPanel) {
                  roleQuestionInAddedPanel.value = singleRole.value; // This should update the UI via onValueChanging -> updateAllPanelsUI
                } else {
                  console.warn(
                    "Role question ('role') not found in the identified added panel.",
                  );
                }
              } else if (configuredRoles.length > 1) {
                const currentQuestionValue = (question.value ||
                  []) as ProfileSearchMultipleQuestionData[];

                const roleCounters: Record<string, number> = {};
                currentQuestionValue.forEach((item) => {
                  if (item.role) {
                    roleCounters[item.role] =
                      (roleCounters[item.role] || 0) + 1;
                  }
                });

                const availableRoles = configuredRoles.filter((r) => {
                  const currentCount = roleCounters[r.value] || 0;
                  const maxCount = r.max ?? Infinity;
                  return currentCount < maxCount;
                });

                if (availableRoles.length === 1) {
                  const lastAvailableRole = availableRoles[0];
                  const roleQuestionInAddedPanel =
                    identifiedAddedPanel.getQuestionByName(
                      "role",
                    ) as QuestionRadiogroupModel;

                  if (roleQuestionInAddedPanel) {
                    roleQuestionInAddedPanel.value = lastAvailableRole.value;
                  } else {
                    console.warn(
                      "Role question ('role') not found in the identified added panel.",
                    );
                  }
                }
              }
            } else {
              console.warn(
                "Custom logic for single role skipped: Could not identify the added panel through event args or fallback.",
              );
            }
          }
        };
      }
    },
    onAfterRender: async (question: QuestionCustomModel, htmlElement) => {
      const survey: any = question.getSurvey();
      const profile = survey.getVariable("profile");

      const styleSubmitter = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.styleSubmitter,
      );
      const submitterClassName = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.submitterClassName,
      );
      const blockContainerClassName = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.blockContainerClassName,
      );

      const panelDynamic = <QuestionPanelDynamicModel>question.contentQuestion;

      const coauthorBlock = question.getPropertyValue(
        ProfileSearchMultipleQuestionProperties.coauthorBlock,
      );

      if (
        question.autoAssignSubmitter !== null &&
        question.autoAssignSubmitter !== undefined
      ) {
        const panelToSet = panelDynamic.panels.find((panel: PanelModel) => {
          const questionsOnPanel = panel.getQuestions(false);
          const roleQuestion = questionsOnPanel.find(
            (question) => question.name === "role",
          );
          return question.autoAssignSubmitter == roleQuestion?.value;
        });
        const profileQuestion = panelToSet?.getQuestionByName(
          CUSTOM_TYPE_PROFILE_SEARCH,
        );

        if (profileQuestion && !profileQuestion!.value!) {
          profileQuestion.value = {
            profileId: profile?.profileId,
            displayName: getDisplayNameHelper(profile),
            affiliation: profile?.additionalInformation?.affiliation ?? "",
            email: defaultEmailFromProfile(profile),
          };
        }
      }

      if (coauthorBlock) {
        const existingHtml = htmlElement.nextElementSibling as HTMLElement;

        if (
          !existingHtml ||
          !existingHtml.classList.contains("coauthor-block-container")
        ) {
          const container = document.createElement("div");
          container.className = `coauthor-block-container coauthor-block-${question.name}`;
          htmlElement.insertAdjacentElement("afterEnd", container);
          generateCoAuthorBlockHTML({
            container,
            question,
            submitterClassName,
            blockContainerClassName,
            profile,
            styleSubmitter,
          });
        }
      }

      await setupMoveButtons(htmlElement, question);
      await setupCustomRemoveButtons(htmlElement, question);
      await updateAllPanelsUI(question);

      survey.onValueChanged.add(async (sender: SurveyModel, options: any) => {
        await updateAllPanelsUI(question);

        if (options.name === question.name) {
          await setupMoveButtons(htmlElement, question);
          await setupCustomRemoveButtons(htmlElement, question);
          const container = htmlElement.nextElementSibling as HTMLElement;

          if (
            container &&
            container.classList.contains("coauthor-block-container")
          ) {
            generateCoAuthorBlockHTML({
              container,
              question,
              submitterClassName,
              blockContainerClassName,
              profile,
              styleSubmitter,
            });
          }
        }
      });
    },
    onValueChanging: (
      question: QuestionCustomModel,
      name: string,
      newValues: ProfileSearchMultipleQuestionData[],
    ) => {
      const panelDynamic = <QuestionPanelDynamicModel>question.contentQuestion;

      const configuredRolesList =
        <IRoleItemValue[]>(
          question.getPropertyValue(
            ProfileSearchMultipleQuestionProperties.role,
          )
        ) ?? [];

      const itemsByRole: Record<string, ProfileSearchMultipleQuestionData[]> =
        {};
      const unassignedItems: ProfileSearchMultipleQuestionData[] = [];

      const valuesToProcess = Array.isArray(newValues) ? newValues : [];

      valuesToProcess.forEach((item) => {
        if (
          item.role &&
          configuredRolesList.some((r) => r.value === item.role)
        ) {
          if (!itemsByRole[item.role]) {
            itemsByRole[item.role] = [];
          }
          itemsByRole[item.role].push(item);
        } else {
          unassignedItems.push(item);
        }
      });

      const sortedAndProcessedValues: ProfileSearchMultipleQuestionData[] = [];
      const roleCounters: Record<string, number> = {};

      configuredRolesList.forEach((configuredRoleDefinition) => {
        const roleValue = configuredRoleDefinition.value;
        if (itemsByRole[roleValue]) {
          itemsByRole[roleValue].forEach((itemInRoleGroup) => {
            if (roleCounters[roleValue] === undefined) {
              roleCounters[roleValue] = 0;
            }
            itemInRoleGroup.position = roleCounters[roleValue]++;
            sortedAndProcessedValues.push(itemInRoleGroup);
          });
        }
      });

      unassignedItems.forEach((item) => {
        sortedAndProcessedValues.push(item);
      });

      const allSelectedRolesInFinalOrder = sortedAndProcessedValues
        .map((item) => item.role)
        .filter(Boolean) as string[];

      if (!session.adminMode) {
        const canAddMoreBasedOnRoleLimits = configuredRolesList.some((r) => {
          const count = allSelectedRolesInFinalOrder.filter(
            (roleValue) => roleValue === r.value,
          ).length;
          return count < (r.max ?? Infinity);
        });
        const globalMaxPanelCount = panelDynamic.maxPanelCount ?? Infinity;
        panelDynamic.allowAddPanel =
          canAddMoreBasedOnRoleLimits &&
          sortedAndProcessedValues.length < globalMaxPanelCount &&
          configuredRolesList.length > 0;
        if (!canAddMoreBasedOnRoleLimits && configuredRolesList.length > 0) {
          panelDynamic.allowAddPanel = false;
        }
      }

      return sortedAndProcessedValues;
    },
  };

  if (
    !ComponentCollection.Instance.getCustomQuestionByName(
      ProfileSearchMultipleQuestionType,
    )
  ) {
    ComponentCollection.Instance.add(multiple_profile_search_object);
  }
};
